import { getBorderStyle, getBorderWidth } from '$lib/global/helper/border';
import { getBorderRadius } from '$lib/global/helper/border-radius';
import { getShadow } from '$lib/global/helper/shadow';

import type {
    BrandBorderRadius,
    BrandBorderStyle,
    BrandBorderWidth,
    BrandShadow,
    Theme,
} from '$lib/types/Theme';

// Remaps brand border-width tokens to CSS outline-width classes.
// Button uses CSS `outline` (not `border`) for its outline style.
const BRAND_BORDER_TO_OUTLINE_WIDTH: Record<BrandBorderWidth, string> = {
    none: 'fun-outline-0',
    thin: 'fun-outline-1',
    medium: 'fun-outline-2',
    thick: 'fun-outline-4',
};

export interface ResolvedButtonStyling {
    borderRadiusClass: string;
    shadowClass: string;
    borderWidthClass: string | undefined;
    borderStyleClass: string | undefined;
    // Outline-button-specific
    outlineWidthClass: string;
    outlineStyle: string | undefined;
}

interface Params {
    theme: Theme | undefined;
    isOutline: boolean;
}

export const resolveButtonStyling = ({ theme, isOutline }: Params): ResolvedButtonStyling => {
    const themeShadowClass = theme?.shadow
        ? getShadow('button', theme.shadow as BrandShadow)
        : undefined;

    const shadowClass =
        themeShadowClass ?? (isOutline ? 'fun-shadow-none' : 'fun-shadow-button-inset');

    const borderRadiusClass = getBorderRadius(
        'button',
        theme?.borderRadius as BrandBorderRadius | undefined,
    );

    const borderWidthClass = theme?.borderWidth
        ? getBorderWidth(theme.borderWidth as BrandBorderWidth)
        : undefined;
    const borderStyleClass = theme?.borderStyle
        ? getBorderStyle(theme.borderStyle as BrandBorderStyle)
        : undefined;

    const brandOutlineWidthClass = theme?.borderWidth
        ? BRAND_BORDER_TO_OUTLINE_WIDTH[theme.borderWidth as BrandBorderWidth]
        : undefined;

    return {
        borderRadiusClass,
        shadowClass,
        // Outline buttons drive width/style via CSS outline, not CSS border — suppress to avoid a
        // parallel default-grey border ring inside the outline.
        borderWidthClass: isOutline ? undefined : borderWidthClass,
        borderStyleClass: isOutline ? undefined : borderStyleClass,
        outlineWidthClass: brandOutlineWidthClass ?? 'fun-outline-[1.5px]',
        outlineStyle: isOutline ? theme?.borderStyle : undefined,
    };
};
